/**
 * Icon Registry for Heroicons
 * 
 * Maps friendly icon names to Heroicons components.
 * This allows us to use semantic names like "document" instead of "DocumentTextIcon"
 * 
 * Usage:
 *   import { getIconComponent } from '@/utils/iconRegistry'
 *   const Icon = getIconComponent('document', 'outline')
 */

import type { Component } from 'vue'
import * as HeroIconsOutline from '@heroicons/vue/24/outline'
import * as HeroIconsSolid from '@heroicons/vue/24/solid'
import AgricultureIcon from '@/components/icons/AgricultureIcon.vue'

/**
 * Icon name mapping: friendly name -> Heroicons component name
 * 
 * Add new mappings here as needed. The key is the friendly name,
 * the value is the Heroicons component name (without "Icon" suffix).
 */
const ICON_NAME_MAP: Record<string, string> = {
  // Navigation & UI
  'home': 'Home',
  'menu': 'Bars3',
  'close': 'XMark',
  'chevron-down': 'ChevronDown',
  'chevron-up': 'ChevronUp',
  'chevron-left': 'ChevronLeft',
  'chevron-right': 'ChevronRight',
  'arrow-left': 'ArrowLeft',
  'arrow-right': 'ArrowRight',
  'arrow-up': 'ArrowUp',
  'arrow-down': 'ArrowDown',
  
  // Documents & Content
  'document': 'DocumentText',
  'document-text': 'DocumentText',
  'journal': 'DocumentText',
  'folder': 'Folder',
  'file': 'Document',
  
  // User & Profile
  'user': 'User',
  'user-circle': 'UserCircle',
  'users': 'Users',
  'profile': 'User',
  
  // Actions
  'plus': 'Plus',
  'minus': 'Minus',
  'check': 'Check',
  'x': 'XMark',
  'trash': 'Trash',
  'edit': 'Pencil',
  'pencil': 'Pencil',
  'save': 'Bookmark',
  'delete': 'Trash',
  'search': 'MagnifyingGlass',
  'filter': 'Funnel',
  'sort': 'ArrowsUpDown',
  
  // Communication
  'mail': 'Envelope',
  'email': 'Envelope',
  'message': 'ChatBubbleLeft',
  'chat': 'ChatBubbleLeft',
  'notification': 'Bell',
  'bell': 'Bell',
  'feedback': 'ChatBubbleLeft',
  'contact': 'Envelope',
  
  // Status & Feedback
  'check-circle': 'CheckCircle',
  'exclamation': 'ExclamationTriangle',
  'exclamation-triangle': 'ExclamationTriangle',
  'warning': 'ExclamationTriangle',
  'error': 'XCircle',
  'info': 'InformationCircle',
  'success': 'CheckCircle',
  'about': 'InformationCircle',
  
  // Data & Analytics
  'chart': 'ChartBar',
  'chart-bar': 'ChartBar',
  'chart-line': 'ChartBar',
  'analytics': 'ChartBar',
  'data': 'TableCells',
  'table': 'TableCells',
  
  // Settings & Configuration
  'settings': 'Cog6Tooth',
  'cog': 'Cog6Tooth',
  'gear': 'Cog6Tooth',
  'admin': 'ShieldCheck',
  'security': 'ShieldCheck',
  'shield-check': 'ShieldCheck',
  
  // Time & Calendar
  'clock': 'Clock',
  'calendar': 'Calendar',
  'time': 'Clock',
  
  // Media & Files
  'image': 'Photo',
  'photo': 'Photo',
  'upload': 'ArrowUpTray',
  'download': 'ArrowDownTray',
  'export': 'ArrowDownTray',
  'import': 'ArrowUpTray',
  'share': 'Share',
  'cloud': 'Cloud',
  
  // Navigation & Location
  'map': 'MapPin',
  'location': 'MapPin',
  'pin': 'MapPin',
  'globe': 'GlobeAlt',
  'world': 'GlobeAlt',
  'airplane': 'PaperAirplane',
  'plane': 'PaperAirplane',
  'paper-airplane': 'PaperAirplane',
  'aviation': 'PaperAirplane',
  
  // Finance & Business
  'currency': 'CurrencyDollar',
  'currency-dollar': 'CurrencyDollar',
  'dollar': 'CurrencyDollar',
  'money': 'CurrencyDollar',
  'credit-card': 'CreditCard',
  'bank': 'BuildingLibrary',
  'line-of-business': 'BuildingOffice',
  'business': 'BuildingOffice',
  
  // Risk & Engineering (for GRAP platform)
  'gauge': 'Gauge',
  'shield': 'ShieldCheck',
  'risk': 'ExclamationTriangle',
  'warning-triangle': 'ExclamationTriangle',
  'alert': 'BellAlert',
  'monitor': 'ComputerDesktop',
  'intelligence': 'CpuChip',
  'cpu-chip': 'CpuChip',
  'cpu': 'CpuChip',
  'light-bulb': 'LightBulb',
  'insight': 'LightBulb',
  
  // Common UI elements
  'eye': 'Eye',
  'eye-slash': 'EyeSlash',
  'lock': 'LockClosed',
  'unlock': 'LockOpen',
  'key': 'Key',
  'link': 'Link',
  'external-link': 'ArrowTopRightOnSquare',
  'login': 'ArrowRightOnRectangle',
  'logout': 'ArrowLeftOnRectangle',
  
  // Forms & Input
  'star': 'Star',
  'star-filled': 'Star',
  'heart': 'Heart',
  'bookmark': 'Bookmark',
  'conditions': 'Heart',
  'interactions': 'ExclamationTriangle',
  
  // Additional common icons
  'play': 'Play',
  'pause': 'Pause',
  'stop': 'Stop',
  'refresh': 'ArrowPath',
  'reload': 'ArrowPath',
  'sync': 'ArrowPath',
  
  // Data & Signals
  'signal': 'Signal',
  'bars-signal': 'Signal',
  'scale': 'Scale',
  'balance': 'Scale',
  
  // Layout & Grid
  'grid': 'Squares2X2',
  'squares': 'Squares2X2',
  'view-grid': 'Squares2X2',
  'platforms': 'Squares2X2',
  
  // Sparkles/Stars
  'sparkles': 'Sparkles',
  
  // Shipping & Cargo
  'cube': 'Cube',
  'package': 'Cube',
  'container': 'Cube',
  'cargo': 'Cube',
  'shipping': 'Cube',
  'marine': 'Cube',
}

/**
 * Custom icon components (not from Heroicons)
 * These are custom SVG components created for specific use cases
 */
const CUSTOM_ICONS: Record<string, Component> = {
  'agriculture': AgricultureIcon,
  'agricultural': AgricultureIcon,
  'farm': AgricultureIcon,
  'crop': AgricultureIcon,
  'plant': AgricultureIcon,
}

/**
 * Get a Heroicons component by friendly name
 * 
 * @param name - Friendly icon name (e.g., 'document', 'user', 'home')
 * @param variant - 'outline' or 'solid' (default: 'outline')
 * @returns Vue component or null if not found
 */
export function getIconComponent(
  name: string,
  variant: 'outline' | 'solid' = 'outline'
): Component | null {
  // Normalize the name (lowercase only, keep hyphens)
  const normalizedName = name.toLowerCase()
  
  // Check custom icons first
  if (normalizedName in CUSTOM_ICONS) {
    return CUSTOM_ICONS[normalizedName]
  }
  
  // Get the Heroicons component name from our mapping
  const heroIconName = ICON_NAME_MAP[normalizedName]
  
  if (!heroIconName) {
    console.warn(`Icon "${name}" not found in registry. Available icons:`, Object.keys(ICON_NAME_MAP))
    return null
  }
  
  // Construct the full component name (e.g., "DocumentTextIcon")
  const componentName = `${heroIconName}Icon`
  
  // Get from the appropriate variant
  const iconSet = variant === 'solid' ? HeroIconsSolid : HeroIconsOutline
  
  // Access the component (Heroicons exports are named exports)
  const component = (iconSet as any)[componentName]
  
  if (!component) {
    console.warn(`Heroicons component "${componentName}" not found in ${variant} variant`)
    return null
  }
  
  return component as Component
}

/**
 * Check if an icon exists in the registry
 */
export function hasIcon(name: string): boolean {
  const normalizedName = name.toLowerCase()
  return normalizedName in CUSTOM_ICONS || normalizedName in ICON_NAME_MAP
}

/**
 * Get all available icon names
 */
export function getAvailableIcons(): string[] {
  return Object.keys(ICON_NAME_MAP)
}
